import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadIcon from "@mui/icons-material/Download";
import TuneIcon from "@mui/icons-material/Tune";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { selectUser } from "../../features/userSlice";
import { renderCabinetDesign } from "./api";
import { CQ_FIELD, CQ_LABEL, Notice } from "./ui";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { Spinner } from "../../components/UI/States";

const SIZE_OPTIONS = [
  { value: "auto", label: "Auto (match source)" },
  { value: "1024x1024", label: "Square (1024 × 1024)" },
  { value: "1536x1024", label: "Landscape (1536 × 1024)" },
  { value: "1024x1536", label: "Portrait (1024 × 1536)" },
];

const LOADER_MESSAGES = [
  "Analyzing the render…",
  "Relighting the scene…",
  "Rebuilding materials and textures…",
  "Adding realistic reflections and shadows…",
  "Removing noise and grain…",
  "Sharpening to 4K…",
  "Final touches…",
];

const LABEL_CLASS = CQ_LABEL;
const FIELD_CLASS = CQ_FIELD;

function EnhanceLoader({ label }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIdx((current) => (current + 1) % LOADER_MESSAGES.length);
    }, 2200);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="app-card rounded-2xl border p-5" aria-live="polite">
      <div className="relative w-full overflow-hidden rounded-xl" style={{ aspectRatio: "4 / 3" }}>
        <div className="app-skeleton absolute inset-0 !rounded-xl" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-4 text-center">
          <Spinner size={36} label={label} />
          <p className="text-sm font-semibold">{label}</p>
          <p key={idx} className="app-muted animate-pulse text-xs">
            {LOADER_MESSAGES[idx]}
          </p>
        </div>
      </div>
      <p className="app-muted mt-3 text-center text-xs">This usually takes up to a minute.</p>
    </div>
  );
}

function CabinetDesignRender() {
  const user = useSelector(selectUser);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("Enhancing your render…");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [alterPrompt, setAlterPrompt] = useState("");

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected && selected.type !== "application/pdf" && !selected.type.startsWith("image/")) {
      setError("Please choose a PDF or image file.");
      setFile(null);
      return;
    }
    setError("");
    setFile(selected);
  };

  const dataUrlToFile = async (dataUrl, filename) => {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type || "image/png" });
  };

  const handleEnhance = async (event) => {
    event.preventDefault();
    if (!user?.userID) {
      setError("Unable to determine the logged-in user.");
      return;
    }
    if (!file) {
      setError("Upload a render to enhance.");
      return;
    }

    setLoading(true);
    setLoadingLabel("Enhancing your render…");
    setError("");
    setResult(null);

    try {
      const data = await renderCabinetDesign(user.userID, file, { prompt, size, mode: "enhance" });
      setResult(data);
    } catch (renderError) {
      setError(renderError?.message || "Failed to enhance render.");
    } finally {
      setLoading(false);
    }
  };

  const handleAlter = async () => {
    if (!result?.image || !user?.userID) return;
    if (!alterPrompt.trim()) {
      setError("Describe the change you want to make.");
      return;
    }

    setLoading(true);
    setLoadingLabel("Applying your change…");
    setError("");

    try {
      const sourceFile = await dataUrlToFile(result.image, "current-render.png");
      const data = await renderCabinetDesign(user.userID, sourceFile, {
        prompt: alterPrompt,
        size,
        mode: "alter",
      });
      setResult(data);
      setAlterPrompt("");
    } catch (alterError) {
      setError(alterError?.message || "Failed to alter render.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setAlterPrompt("");
    setError("");
  };

  return (
    <PageShell size="md">
      <PageHeader
        icon={AutoAwesomeIcon}
        tone="amber"
        eyebrow="Cabinets"
        title="AI Render Enhancer"
        description="Upload an existing render from your design software and the AI will make it more photorealistic — same scene, better lighting, materials, and detail, upscaled toward 4K."
      />

      <div className="space-y-6">
      <form onSubmit={handleEnhance} className="app-card space-y-5 rounded-2xl border p-5">
        <div>
          <span className={LABEL_CLASS}>Render (image or PDF)</span>
          <div className="app-surface flex flex-col gap-4 rounded-2xl border border-dashed p-4 sm:flex-row sm:items-center">
            <span className="app-icon-tile bg-amber-500/15 text-amber-600 dark:text-amber-300">
              <UploadFileIcon />
            </span>
            <div className="min-w-0 flex-1">
              <p className={`truncate text-sm ${file ? "font-medium" : "app-muted"}`}>
                {file ? file.name : "No file selected"}
              </p>
              <p className="app-muted text-xs">PDF or image</p>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="app-btn app-btn-secondary shrink-0"
            >
              <UploadFileIcon fontSize="small" />
              Choose file
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        <div>
          <label htmlFor="cdr-style" className={LABEL_CLASS}>Style direction (optional)</label>
          <textarea
            id="cdr-style"
            className={FIELD_CLASS}
            rows={3}
            placeholder="e.g. Warmer daylight, richer wood grain, softer shadows, glossier countertop."
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
        </div>

        <div className="max-w-xs">
          <label htmlFor="cdr-size" className={LABEL_CLASS}>Output size</label>
          <select id="cdr-size" className={FIELD_CLASS} value={size} onChange={(event) => setSize(event.target.value)}>
            {SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <Notice tone="rose" role="alert">
            {error}
          </Notice>
        )}

        <div className="app-divider border-t pt-4">
          <button
            type="submit"
            disabled={loading}
            className="app-btn app-btn-primary app-btn-lg"
          >
            <AutoAwesomeIcon fontSize="small" />
            {loading ? "Working…" : "Enhance render"}
          </button>
        </div>
      </form>

      {loading && <EnhanceLoader label={loadingLabel} />}

      {!loading && result && (
        <div className="app-card space-y-4 rounded-2xl border p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Enhanced render</h2>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleReset}
                className="app-btn app-btn-ghost"
              >
                <RestartAltIcon fontSize="small" />
                Start over
              </button>
              <a
                href={result.image}
                download={`enhanced-${(result.source_file || "render").replace(/\.[^.]+$/, "")}.jpg`}
                className="app-btn app-btn-secondary"
              >
                <DownloadIcon fontSize="small" />
                Download PNG
              </a>
            </div>
          </div>

          <img
            src={result.image}
            alt="AI enhanced cabinet render"
            className="app-divider w-full rounded-xl border"
          />

          <div className="app-divider border-t pt-4">
            <label htmlFor="cdr-alter" className={LABEL_CLASS}>Alter this render</label>
            <p className="app-muted mb-2 text-xs">
              Describe a specific change — everything else stays the same. e.g. "make the countertop white
              marble", "warmer lighting", "add under-cabinet lighting".
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="cdr-alter"
                type="text"
                className={FIELD_CLASS}
                placeholder="Describe the change you want…"
                value={alterPrompt}
                onChange={(event) => setAlterPrompt(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAlter();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAlter}
                disabled={loading}
                className="app-btn app-btn-primary"
              >
                <TuneIcon fontSize="small" />
                Apply change
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </PageShell>
  );
}

export default CabinetDesignRender;
